import { useSelector } from "react-redux";
import { type RootStateI } from "../index";
import { type Dispatch } from "redux";
import type SubscriptionPlanDTO from "@tootsweet/model/datatransfer/SubscriptionPlanDTO";
import { fetchSubscriptionPlan } from "../../app/17_JetpackOnboarding/components/CheckoutSubscription/api";
import { type DateValueI } from "../../app/19_JetpackConfig/types";
import { sortByPriceAndNoPriceFlag } from "../../utils/helpers/sortSubscriptionPlan";
import OnboardingAPI from "../../app/03_Onboarding/helpers/OnboardingAPI";
import { setEvents, setPOSAPrices, setPOSATypes } from "./wheel";
import CurrencySubUnitsMap from "../../model/CurrencySubUnitsMap";

export const SET_DEBUG = "SET_DEBUG";
export const SET_LOADING = "SET_LOADING";
export const SET_IS_MOBILE = "SET_IS_MOBILE";
export const SET_ERROR = "SET_ERROR";
export const SET_SUCCESS = "SET_SUCCESS";
export const SET_INFO = "SET_INFO";
export const SET_WINDOW_WIDTH = "SET_WINDOW_WIDTH";
export const SET_SUBSCRIPTION = "SET_SUBSCRIPTION";
export const SET_STRIPE_CLIENT_SECRET = "SET_STRIPE_CLIENT_SECRET";
export const SET_DATE_RANGE = "SET_DATE_RANGE";
export const SET_SUBSCRIPTION_PLANS = "SET_SUBSCRIPTION_PLANS";
export const SET_CURRENCY = "SET_CURRENCY";
export const SET_COUNTRY_CODE = "SET_COUNTRY_CODE";
export const SET_CURRENCY_SUB_UNITS = "SET_CURRENCY_SUB_UNITS";

export const setDebug = (isDebug: boolean) => {
  return {
    type: SET_DEBUG,
    isDebug
  };
};

export const setLoading = (isLoading: boolean) => {
  return {
    type: SET_LOADING,
    isLoading
  };
};

export const setIsMobile = (isMobile: boolean) => {
  return {
    type: SET_IS_MOBILE,
    isMobile
  };
};

export const setError = (error: string) => {
  return {
    type: SET_ERROR,
    error
  };
};

export const setSuccess = (success: string) => {
  return {
    type: SET_SUCCESS,
    success
  };
};

export const setInfo = (info: string) => {
  return {
    type: SET_INFO,
    info
  };
};

export const setSubscriptionPlans = (
  subscriptionPlans: SubscriptionPlanDTO[] | undefined
) => {
  return {
    type: SET_SUBSCRIPTION_PLANS,
    subscriptionPlans
  };
};

export const setCurrencySubUnits = (currencySubUnits: CurrencySubUnitsMap) => {
  return {
    type: SET_CURRENCY_SUB_UNITS,
    currencySubUnits
  };
};

export const setCurrency = (currency: string) => {
  return {
    type: SET_CURRENCY,
    currency
  };
};

export const setCountryCode = (countryCode: string) => {
  return {
    type: SET_COUNTRY_CODE,
    countryCode
  };
};

export const setDateRange = (dateRange: DateValueI) => {
  return {
    type: SET_DATE_RANGE,
    dateRange
  };
};

export const fetchEventsAndPOSAData = () => async (dispatch: Dispatch) => {
  const [posaTypes, posaPricesWithMarkup, events] = await Promise.all([
    OnboardingAPI.getAllPOSATypes(),
    OnboardingAPI.getAllPOSAPricesWithMarkup(),
    OnboardingAPI.getEvents()
  ]);
  dispatch(setPOSATypes(posaTypes));
  dispatch(setEvents(events));
  dispatch(setPOSAPrices(posaPricesWithMarkup));
};

export const fetchAllSubscriptionPlans = () => async (dispatch: Dispatch) => {
  try {
    const response = await fetchSubscriptionPlan();
    const sortedPlans = sortByPriceAndNoPriceFlag(response.plans);
    // don't change this order
    dispatch(setCurrency(response.currency));
    dispatch(setCountryCode(response.countryCode));
    dispatch(setCurrencySubUnits(response.currencySubUnits));
    dispatch(setSubscriptionPlans(sortedPlans));
  } catch (e) {
    console.error(e);
  }
};

export const useSubscriptionPlansSelector = () => {
  return useSelector<RootStateI, SubscriptionPlanDTO[] | undefined>(
    (s) => s.app.subscriptionPlans
  );
};

export const useCurrencySelector = () => {
  return useSelector<RootStateI, string>((s) => s.app.currency);
};

export const useCountryCodeSelector = () => {
  return useSelector<RootStateI, string>((s) => s.app.countryCode);
};

export const useCurrencySubUnitsMapSelector = () => {
  return useSelector<RootStateI, CurrencySubUnitsMap>(
    (s) => s.app.currencySubUnits
  );
};

export const useLoadingSelector = () => {
  return useSelector<RootStateI, boolean>((s) => s.app.isLoading);
};

export const useErrorSelector = () => {
  return useSelector<RootStateI, string>((s) => s.app.error);
};

export const useSuccessSelector = () => {
  return useSelector<RootStateI, string>((s) => s.app.success);
};

export const useInfoSelector = () => {
  return useSelector<RootStateI, string>((s) => s.app.info);
};

export const useDateRangeSelector = () => {
  return useSelector<RootStateI, DateValueI>((s) => s.app.dateRange);
};
